import type { CSSProperties, ReactNode } from 'react';
import { COULEURS } from '../../app/styles.js';
import { ACCESSIBILITE, STATUTS_CONTACT } from './libelles.js';
import { anneeDeces, generationDe, nomAffiche, quotePart } from './modele.js';
import type { DonneesArbre, Personne, StatutContact } from './types.js';

export interface Affichage {
  quotesParts?: boolean;
  defuntsGrises?: boolean;
}

export interface ContexteNoeud {
  donnees: DonneesArbre;
  focusId?: string | undefined;
  vue: 'equipe' | 'famille';
  afficher: Required<Affichage>;
  /** Statut de contact de chaque personne, vue famille seulement. */
  statuts?: Readonly<Record<string, StatutContact>> | undefined;
  /** Personne qui consulte (affichée « vous »), vue famille seulement. */
  viewerId?: string | undefined;
  /** Équipe seulement : ajoute un frère ou une sœur de la personne (mêmes parents, ADR 0064). */
  onAjouterFratrie?: ((id: string) => void) | undefined;
}

const pays = (code: string): string => {
  try {
    return new Intl.DisplayNames(['fr'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
};

/** Texte de la pastille : nom, croix et année des défunts, hypothèse, résidence à l'étranger, quote-part, état. */
export function EtiquettePersonne({ p, ctx }: { p: Personne; ctx: ContexteNoeud }) {
  const { donnees: d } = ctx;
  const annee = anneeDeces(p);
  const residence = d.residences?.[p.id];
  const quote = ctx.afficher.quotesParts && ctx.vue === 'equipe' ? quotePart(d, p.id) : null;
  const statut = ctx.statuts?.[p.id];
  const vivantIdentifie = p.vitalStatus === 'vivant' && !p.isHypothesis;
  return (
    <>
      <strong>{nomAffiche(p)}</strong>
      {p.vitalStatus === 'decede' ? <span> † {annee ?? 'date inconnue'}</span> : null}
      {p.isHypothesis && !residence ? <span> (hypothèse)</span> : null}
      {residence ? (
        <span>
          {' '}
          · {pays(residence.pays)}
          {residence.certitude === 'hypothese' ? ' ?' : ''}
        </span>
      ) : null}
      {quote ? <span> · {quote}</span> : null}
      {ctx.vue === 'equipe' && vivantIdentifie ? <span> · {ACCESSIBILITE[p.reachability]}</span> : null}
      {ctx.vue === 'famille' && ctx.viewerId === p.id ? <span> (vous)</span> : null}
      {ctx.vue === 'famille' && ctx.viewerId !== p.id && statut && statut !== 'defunt' ? (
        <span data-contact={statut}> · {STATUTS_CONTACT[statut]}</span>
      ) : null}
      {ctx.vue === 'famille' && statut === 'accepte' && ctx.viewerId !== p.id
        ? p.contacts.map((c) => (
            <span key={c.id} data-coordonnee={c.type}>
              {' '}
              · {c.value}
            </span>
          ))
        : null}
    </>
  );
}

/** Taille du texte : 18 px au minimum côté client (UX-20), 14 px dans l'outil de l'équipe. */
export const taillePolice = (vue: 'equipe' | 'famille'): number => (vue === 'famille' ? 18 : 14);

/** Contour du nœud sélectionné : marine foncé sur blanc, contraste supérieur à 3:1 (WCAG 1.4.11). */
export const CONTOUR_SELECTION = `3px solid ${COULEURS.marineFonce}`;

const styleNoeud = (p: Personne, ctx: ContexteNoeud): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 2,
  minHeight: 44,
  padding: '6px 12px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.bordure}`,
  background: COULEURS.blanc,
  color: COULEURS.marineFonce,
  fontSize: taillePolice(ctx.vue),
  ...(ctx.focusId === p.id ? { outline: CONTOUR_SELECTION, outlineOffset: 2 } : {}),
  opacity: p.vitalStatus === 'decede' && ctx.afficher.defuntsGrises ? 0.7 : 1,
  borderStyle: p.isHypothesis ? 'dashed' : 'solid',
});

/** Un nœud de l'arbre : élément d'arbre atteignable au clavier (un seul à la fois au focus, les autres aux flèches). */
export function Noeud({
  p,
  ctx,
  balise = 'div',
  children,
}: {
  p: Personne;
  ctx: ContexteNoeud;
  balise?: 'div' | 'li';
  children?: ReactNode;
}) {
  const Balise = balise;
  const focus = ctx.focusId;
  const grise = p.vitalStatus === 'decede' && ctx.afficher.defuntsGrises;
  const proprietes = {
    role: 'treeitem',
    'data-noeud': p.id,
    'aria-level': generationDe(ctx.donnees, p.id) + 1,
    'aria-selected': focus === undefined ? undefined : focus === p.id,
    tabIndex: focus === undefined ? undefined : focus === p.id ? 0 : -1,
    'data-grise': grise ? 'true' : undefined,
  } as const;
  if (balise === 'li') {
    return (
      <li {...proprietes} style={{ listStyle: 'none', margin: '4px 0' }}>
        <span style={styleNoeud(p, ctx)}>
          <EtiquettePersonne p={p} ctx={ctx} />
        </span>
        {children}
      </li>
    );
  }
  return (
    <Balise {...proprietes} style={styleNoeud(p, ctx)}>
      <EtiquettePersonne p={p} ctx={ctx} />
      {children}
    </Balise>
  );
}
